import { Component, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { addDays, format } from 'date-fns';
import { ProgramCompletionSummaryComponent } from '@lb/components/program-completion-summary/program-completion-summary.component';
import { EnrollmentTagComponent } from '@lb/components/enrollment-tag/enrollment-tag.component';
import { MDContentBoxComponent, MDIconComponent, MDTagComponent } from '@melodic-ui/components';
import { LoaderComponent } from '@lb/components/loader/loader.component';
import { EnrollmentNotificationsStateService } from '../enrollment-notifications/enrollment-notifications-state.service';
import { GlobalTimezoneService } from '@lb/services/global-timezone.service';
import { TimezoneStateService } from '@lb/state/timezone-state.service';
import { LessonDetailStateService } from '@lb/components/lesson-detail/lesson-detail-state.service';
import { EnrollmentDetailStateService } from '../enrollment-detail-state.service';
import { computeProgramSummary } from '@lb/functions/program-summary.util';
import { convertLessonDateForDisplay } from '@lb/functions/timezone-utils';
import { buildLessonTimeSlots, ILessonTimeSlot } from '@lb/components/lessons/utils/lesson-time-slots.util';
import { USER_VIEW_TOKEN } from '../enrollment-detail.tokens';
import { GradingTemplateCategoryType } from '@lb/api/core/interfaces';
import { MDProgressBarComponent } from '@melodic-ui/components';
import { UserStateService } from '@lb/state/user-state.service';
import { CopyToClipboardComponent, ProgramTypeTagComponent, UserIconComponent } from '@lb/components';
import { TwoColumnPageLayoutComponent } from '@lb/components/page-templates/two-column-page-layout/two-column-page-layout.component';
import { IUserSummary } from '@lb/api/mylb/interfaces';
import { NoInstructorComponent } from '@lb/components/no-instructor/no-instructor.component';
import { EnrollmentStatus } from '@lb/api/mylb/enums/enrollment-status.enum';
import { FinalGradesSummaryComponent } from '../final-grades-summary/final-grades-summary.component';
import { EnrollmentNotificationsComponent } from '../enrollment-notifications/enrollment-notifications.component';
import { AccountStateService } from '@lb/state/account-state.service';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { buildAdminUrl } from '@lb/config/utils/admin-url.util';

@Component({
	selector: 'lb-enrollment-overview',
	standalone: true,
	imports: [
		CommonModule,
		ProgramCompletionSummaryComponent,
		EnrollmentTagComponent,
		MDContentBoxComponent,
		MDIconComponent,
		MDTagComponent,
		MDProgressBarComponent,
		LoaderComponent,
		ProgramTypeTagComponent,
		TwoColumnPageLayoutComponent,
		UserIconComponent,
		NoInstructorComponent,
		CopyToClipboardComponent,
		FinalGradesSummaryComponent,
		EnrollmentNotificationsComponent
	],
	providers: [EnrollmentNotificationsStateService],
	templateUrl: './enrollment-overview.component.html',
	styleUrl: './enrollment-overview.component.scss'
})
export class EnrollmentOverviewComponent {
	private readonly enrollmentDetailStateService = inject(EnrollmentDetailStateService);
	private readonly lessonStateService = inject(LessonDetailStateService);
	private readonly _timezoneStateService = inject(TimezoneStateService);
	private readonly _globalTimezoneService = inject(GlobalTimezoneService);
	private readonly _userStateService = inject(UserStateService);
	private readonly _accountStateService = inject(AccountStateService);
	private readonly _appSettings = inject<IAppSettings>(APP_CONFIG);

	private readonly _availableTimezones = this._timezoneStateService.timezones;

	protected readonly userView = inject(USER_VIEW_TOKEN, { optional: true }) ?? 'learner';

	enrollmentDetail = this.enrollmentDetailStateService.enrollmentDetail;
	gradingTemplate = this.enrollmentDetailStateService.gradingTemplate;
	loading = this.enrollmentDetailStateService.loading;
	lessons = this.lessonStateService.relatedLessons;
	lessonsLoading = this.lessonStateService.relatedLessonsLoading;

	learner = computed(() => this._userStateService.getUserSummary(this.enrollmentDetail()?.learnerUserID ?? 0)());
	instructor = computed(() => {
		const instructorID = this.enrollmentDetail()?.instructorUserID;
		if (!instructorID) return null;

		return this._userStateService.getUserSummary(instructorID)();
	});
	viewingUser = computed<IUserSummary | null>(() => (this.userView !== 'instructor' ? this.instructor() : this.learner()));

	/**
	 * The learner's public tags (type IsAdminOnly = 0), off the same cached summary as the name. Rendered
	 * only when this box shows the learner (the instructor's view); the learner's view shows the instructor,
	 * whose public tags have no UI yet.
	 */
	learnerTags = computed(() => (this.userView === 'instructor' ? this.learner()?.tags : undefined) ?? []);

	/**
	 * Which person the card shows — the inverse of whose view this is, matching the label the template
	 * renders above the name. Also picks which admin detail route the row links to.
	 */
	private readonly _viewingUserType: 'learner' | 'instructor' = this.userView !== 'instructor' ? 'instructor' : 'learner';

	/** Admins are the only audience with access to the detail pages, and only once the summary resolves. */
	protected readonly canOpenUserDetail = computed(() => this._accountStateService.isAdmin() && !!this.viewingUser()?.userID);

	programSummary = computed(() => computeProgramSummary(this.enrollmentDetail(), this.lessons()));

	isAwaitingSchedule = computed(() => this.enrollmentDetail()?.enrollmentStatusID === EnrollmentStatus.AwaitingSchedule);
	isAwaitingMatch = computed(() => this.enrollmentDetail()?.enrollmentStatusID === EnrollmentStatus.AwaitingMatch);
	programName = computed(() => this.enrollmentDetail()?.programName);
	programType = computed(() => this.enrollmentDetail()?.programTypeName);

	// Milestones are defined once on the enrollment's grading template — the API has no
	// per-semester milestone definitions (only per-semester *progress*), so the denominator
	// is shared across semesters. Distinct per-semester sets would need a backend change.
	private readonly _projectCategory = computed(
		() => this.gradingTemplate()?.categories.find((category) => category.categoryType === GradingTemplateCategoryType.Project) ?? null
	);

	// Empty when the enrollment has no project category (or the template hasn't loaded yet);
	// the template hides the Project Progress section in that case.
	semesterProjectProgress = computed(() => {
		const projectCategory = this._projectCategory();
		if (!projectCategory) return [];

		const totalMilestones = projectCategory.milestones?.length ?? 0;
		const semesters = [...(this.enrollmentDetail()?.semesters ?? [])].sort((a, b) => a.semesterNumber - b.semesterNumber);

		return semesters.map((semester) => {
			const progress = semester.projectDetail?.projectProgress ?? [];
			const completedMilestones = new Set(progress.map((milestone) => milestone.projectMilestoneID)).size;
			const percent = totalMilestones === 0 ? 0 : Math.round((completedMilestones / totalMilestones) * 100);
			return {
				semesterID: semester.semesterID,
				label: `Semester ${semester.levelName}`,
				percent,
				progressLabel: `${completedMilestones}/${totalMilestones}`
			};
		});
	});
	// Sidebar info
	relatedInstitution = computed(() => this.enrollmentDetail()?.institutionName);

	mustStartBy = computed(() => {
		const d = this.enrollmentDetail()?.mustStartBy;
		return d ? format(new Date(d), 'd MMM yyyy') : null;
	});

	programCompletionDate = computed(() => {
		const expiration = this.enrollmentDetail()?.programExpiration;
		const startRaw = this.enrollmentDetail()?.startDate;
		if (!expiration || !startRaw) return null;
		const tz = this._globalTimezoneService.effectiveTimezone();
		return format(addDays(convertLessonDateForDisplay(new Date(startRaw), tz), expiration), 'd MMM yyyy');
	});

	// First/Last Lesson Date are UTC instants from the backend. Convert to the user's selected
	// timezone (same signal the timeline pipe reads) before formatting so all views agree.
	// Null when the enrollment has no date yet — the template renders a placeholder.
	private _startDateRaw = computed(() => {
		const dateVal = this.enrollmentDetail()?.startDate;
		const tz = this._globalTimezoneService.effectiveTimezone();
		return dateVal ? convertLessonDateForDisplay(new Date(dateVal), tz) : null;
	});

	startDate = computed(() => {
		const d = this._startDateRaw();
		return d ? format(d, 'd MMM yyyy') : null;
	});

	private _endDateRaw = computed(() => {
		const dateVal = this.enrollmentDetail()?.endDate;
		const tz = this._globalTimezoneService.effectiveTimezone();
		return dateVal ? convertLessonDateForDisplay(new Date(dateVal), tz) : null;
	});

	endDate = computed(() => {
		const d = this._endDateRaw();
		return d ? format(d, 'd MMM yyyy') : null;
	});

	constructor() {
		effect(() => {
			const enrollmentInfo = this.enrollmentDetail();
			if (!enrollmentInfo) return;

			const userIDs = [enrollmentInfo.instructorUserID, enrollmentInfo.learnerUserID].filter((id): id is number => id != null);
			if (userIDs.length) {
				this._userStateService.loadUserSummaries(userIDs);
			}
		});
	}

	timeSlotSelections = computed<ILessonTimeSlot[]>(() =>
		buildLessonTimeSlots({
			schedules: this.enrollmentDetail()?.enrollmentSchedules ?? [],
			availableTimezones: this._availableTimezones(),
			selectedTimezone: this._globalTimezoneService.selectedTimezone(),
			fallbackTimezoneID: this.learner()?.timezoneID
		})
	);

	/**
	 * The learner's timezone, formatted as the admin summary formats it ("Eastern Time: New York (EST)").
	 * Worth stating explicitly because the lesson-time tags above are converted to whatever timezone the
	 * viewer has selected — this row is what says which timezone the schedule was actually agreed in.
	 * Null when the learner's summary or timezone hasn't resolved; the template renders a placeholder.
	 */
	learnerTimeZone = computed(() => {
		const timeZones = this._availableTimezones();
		const timeZoneID = this.learner()?.timezoneID;

		if (!timeZones?.length || !timeZoneID) {
			return null;
		}

		const timeZone = timeZones.find((tz) => tz.timeZoneID === Number(timeZoneID));
		return timeZone ? `${timeZone.displayName}: ${timeZone.city} (${timeZone.abbreviation})` : null;
	});

	/**
	 * Opens the shown person's admin detail page in a new tab, as the admin enrollment summary's people
	 * card does. The URL is absolute rather than a routerLink because this component also renders inside
	 * the learner and instructor apps, where the admin routes don't exist.
	 */
	protected openUserDetail(): void {
		if (!this.canOpenUserDetail()) return;

		const url = buildAdminUrl(this._appSettings.adminAppUrl, `${this._viewingUserType}/${this.viewingUser()?.userID}/basic-information`);
		window.open(url, '_blank', 'noopener,noreferrer');
	}
}
